import { type I18n } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import {
  ActivityIcon,
  BellIcon,
  Building2Icon,
  CalendarClockIcon,
  CalendarIcon,
  ChartColumnIcon,
  CirclePlusIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  ClipboardPlusIcon,
  CpuIcon,
  DatabaseIcon,
  FileSearchIcon,
  FileTextIcon,
  FileUpIcon,
  FlaskConicalIcon,
  HeartPulseIcon,
  IdCardIcon,
  LayoutDashboardIcon,
  MicroscopeIcon,
  MonitorIcon,
  PillIcon,
  ScanSearchIcon,
  ScrollTextIcon,
  Settings2Icon,
  StethoscopeIcon,
  TableIcon,
  TestTubeIcon,
  TrendingUpIcon,
  UserCogIcon,
  UsersIcon,
  WrenchIcon,
} from "lucide-react";
import { matchPath } from "react-router-dom";

import { PermissionBit, UserFeatureFlag } from "@/api";
import { clinicalStudies } from "@/constants/enum-to-text";
import {
  hasAdminAccess,
  hasClinicalStudiesAccess,
  hasMonitoringAccess,
  hasPermission,
} from "@/constants/permissions";

import { AppModel } from "../../app-model";
import { pageTitles } from "../../page-titles";
import { RAW_DATA_SECTIONS } from "../../raw-data";
import { isUnder, MenuItem } from "./menu-list";

/**
 * Single source of truth for navigation: the whole tree is derived from the app
 * state and the current URL, so a section like Admin can be browsed from
 * anywhere. Sections tied to the entity you're viewing (the current patient or
 * study) are filled in only on that route.
 *
 * Rebuilt on every render, so labels stay current as long as the caller's
 * `i18n` comes from `useLingui()` (which re-renders on locale change).
 */
export function buildMenu(
  app: AppModel,
  pathname: string,
  i18n: I18n,
): MenuItem[] {
  const permissions = app.seerlinqApi.permissions;
  const monitoring = hasMonitoringAccess(permissions);
  const admin = hasAdminAccess(permissions);
  const studies = hasPermission(permissions, PermissionBit.CLINICAL_STUDIES);

  // `app.patient` is the single source for the current patient (id, name,
  // enrollments). matchPath only gates it to patient routes, since app.patient
  // lingers after you navigate away.
  const patientId = matchPath({ path: "/patient", end: false }, pathname)
    ? (app.patient?.patientId ?? null)
    : null;
  const study =
    matchPath({ path: "/clinical-studies/:name", end: false }, pathname)?.params
      .name ?? null;

  // A dot on Events while unsaved rows wait on some other tab. Deliberately not
  // counted while that tab is open: the rows are on screen there, and reading
  // the draft would re-render the whole menu on every keystroke.
  const eventsPath = `/patient/${patientId}/events`;
  const unsavedEvents =
    patientId !== null &&
    !isUnder(eventsPath, pathname) &&
    (app.patient?.eventsDraft.startedRowCount() ?? 0) > 0
      ? i18n._(msg`Unsaved events`)
      : undefined;

  // Patient uploads waiting for a reviewer, counted on Medical reports.
  const awaitingReview = app.patient?.awaitingReviewCount ?? 0;

  return [
    {
      to: "/",
      label: i18n._(pageTitles.listOfPatients),
      icon: UsersIcon,
      // The current patient's pages nest here, since there's no main item for
      // one. They sit directly under it rather than under a node for the
      // patient, which keeps them one level shallower — the patient is named by
      // the page's own header and title, so the nav does not repeat it.
      children:
        patientId === null
          ? undefined
          : [
              {
                to: `/patient/${patientId}`,
                label: i18n._(pageTitles.trends),
                icon: TrendingUpIcon,
              },
              {
                to: `/patient/${patientId}/measurements`,
                label: i18n._(pageTitles.measurements),
                icon: TableIcon,
              },
              {
                to: `/patient/${patientId}/physicians`,
                label: i18n._(pageTitles.physicians),
                icon: StethoscopeIcon,
                show: monitoring,
              },
              {
                to: `/patient/${patientId}/alerts`,
                label: i18n._(pageTitles.alerts),
                icon: BellIcon,
                show: hasPermission(permissions, PermissionBit.VIEW_ALERTS),
                children: [
                  {
                    to: `/patient/${patientId}/alerts/thresholds`,
                    label: i18n._(pageTitles.thresholds),
                    show: hasPermission(
                      permissions,
                      PermissionBit.VIEW_THRESHOLDS,
                    ),
                  },
                ],
              },
              {
                to: eventsPath,
                label: i18n._(pageTitles.events),
                icon: CalendarClockIcon,
                dot: unsavedEvents,
              },
              {
                to: `/patient/${patientId}/medications`,
                label: i18n._(pageTitles.medications),
                icon: PillIcon,
              },
              {
                to: `/patient/${patientId}/diags`,
                label: i18n._(pageTitles.comorbidities),
                icon: ClipboardPlusIcon,
              },
              {
                to: `/patient/${patientId}/labs`,
                label: i18n._(pageTitles.labs),
                icon: TestTubeIcon,
                show: monitoring,
              },
              {
                to: `/patient/${patientId}/exams`,
                label: i18n._(pageTitles.exams),
                icon: ScanSearchIcon,
                show: monitoring,
              },
              {
                to: `/patient/${patientId}/basics`,
                label: i18n._(pageTitles.basicMedicalData),
                icon: IdCardIcon,
              },
              {
                to: `/patient/${patientId}/any`,
                label: i18n._(pageTitles.addAnyData),
                icon: CirclePlusIcon,
                show: monitoring,
              },
              {
                to: `/patient/${patientId}/healthpro`,
                label: i18n._(pageTitles.healthProReporting),
                icon: FileUpIcon,
                show: monitoring,
              },
              {
                to: `/patient/${patientId}/reports`,
                label: i18n._(pageTitles.medicalReports),
                icon: FileTextIcon,
                count:
                  awaitingReview > 0
                    ? {
                        value: awaitingReview,
                        label: plural(awaitingReview, {
                          one: "# patient upload awaiting review",
                          other: "# patient uploads awaiting review",
                        }),
                      }
                    : undefined,
              },
              {
                to: `/patient/${patientId}/logs`,
                label: i18n._(pageTitles.logs),
                icon: ScrollTextIcon,
                show: admin,
              },
              {
                to: `/patient/${patientId}/questionnaire`,
                label: i18n._(pageTitles.questionnaires),
                icon: ClipboardListIcon,
              },
              {
                to: `/patient/${patientId}/clinical-studies`,
                label: i18n._(pageTitles.clinicalStudies),
                icon: MicroscopeIcon,
                show: hasClinicalStudiesAccess(permissions),
                children: (app.patient?.enrollments ?? []).map((e) => ({
                  to: `/patient/${patientId}/clinical-studies/${e.study_acronym}`,
                  label: clinicalStudies[e.study_acronym],
                })),
              },
              // Transitional home for the per-type tables the Measurements
              // page consolidates. `defaultExpanded` is deliberately omitted:
              // that leaves it collapsed unless the active route is already
              // inside it, exactly as Clinical studies behaves.
              {
                to: `/patient/${patientId}/raw`,
                label: i18n._(pageTitles.rawData),
                icon: DatabaseIcon,
                show: admin,
                // Order and per-section visibility come from the shared table,
                // so `/raw` can redirect to the first entry this user sees
                // without re-deriving the rules.
                children: RAW_DATA_SECTIONS.map((section) => ({
                  to: `/patient/${patientId}/raw/${section.segment}`,
                  label: i18n._(section.title),
                  show: section.show?.(permissions),
                })),
              },
            ],
    },
    {
      to: "/med-waiting-room",
      label: i18n._(pageTitles.medWaitingRoom),
      icon: HeartPulseIcon,
      show: app.seerlinqApi.canViewWaitingOrAuditRoom,
    },
    {
      to: "/tech-waiting-room",
      label: i18n._(pageTitles.techWaitingRoom),
      icon: CpuIcon,
      show: app.seerlinqApi.canViewWaitingOrAuditRoom,
    },
    {
      to: "/audit-room",
      label: i18n._(pageTitles.auditRoom),
      icon: ClipboardCheckIcon,
      show: app.seerlinqApi.canViewWaitingOrAuditRoom,
    },
    {
      to: "/extraction-queue",
      label: i18n._(pageTitles.extractionQueue),
      icon: FileSearchIcon,
      // Not the waiting-room permission the rooms above share: reviewing
      // extractions across patients is monitoring-level work, and a user
      // without that group only gets a 403 from the queue's endpoint.
      show: monitoring,
    },
    {
      to: "/daily-monitoring",
      label: i18n._(pageTitles.dailyMonitoring),
      icon: ActivityIcon,
      show:
        monitoring &&
        app.seerlinqApi.hasFeatureFlag(
          UserFeatureFlag.fe_daily_monitoring_dashboard,
        ),
    },
    {
      to: "/calendar",
      label: i18n._(pageTitles.calendar),
      icon: CalendarIcon,
      show: studies,
    },
    {
      to: "/clinical-studies",
      label: i18n._(pageTitles.clinicalStudies),
      icon: FlaskConicalIcon,
      show: studies,
      children:
        study === null
          ? undefined
          : [
              {
                to: `/clinical-studies/${study}`,
                label: i18n._(pageTitles.definition),
                icon: FileTextIcon,
              },
              {
                to: `/clinical-studies/${study}/overview`,
                label: i18n._(pageTitles.overview),
                icon: LayoutDashboardIcon,
              },
            ],
    },
    {
      to: "/admin",
      label: i18n._(pageTitles.admin),
      icon: WrenchIcon,
      show: monitoring,
      children: [
        {
          to: "/admin/clinics",
          label: i18n._(pageTitles.clinics),
          icon: Building2Icon,
        },
        {
          to: "/admin/physicians",
          label: i18n._(pageTitles.physicians),
          icon: StethoscopeIcon,
        },
        {
          to: "/admin/users",
          label: i18n._(pageTitles.users),
          icon: UserCogIcon,
          show: monitoring,
        },
        {
          to: "/admin/monitoring-management",
          label: i18n._(pageTitles.monitoringManagement),
          icon: MonitorIcon,
          show: monitoring,
        },
        {
          to: "/admin/analytics",
          label: i18n._(pageTitles.analytics),
          icon: ChartColumnIcon,
          show: admin,
        },
        {
          to: "/admin/settings",
          label: i18n._(pageTitles.settings),
          icon: Settings2Icon,
          show: admin,
          defaultExpanded: true,
          children: [
            {
              to: "/admin/settings/monitoring",
              label: i18n._(pageTitles.monitoring),
            },
            {
              to: "/admin/settings/questionnaires",
              label: i18n._(pageTitles.questionnaires),
            },
            {
              to: "/admin/settings/consents",
              label: i18n._(pageTitles.consents),
            },
          ],
        },
      ],
    },
  ];
}
